import React from 'react';
import { CheckCircle, Star, Shield, Headphones } from 'lucide-react';

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    description: 'Perfect for getting started',
    features: ['100 leads/month', '500 emails', 'Community support'],
    badge: null,
    cardClass: 'bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300',
    textColor: 'text-green-900',
    priceColor: 'text-green-700',
    buttonClass: 'bg-green-600 hover:bg-green-700 text-white',
    selectedClass: 'ring-4 ring-green-400 ring-offset-2'
  },
  {
    id: 'basic',
    name: 'Basic',
    price: 163,
    description: 'Great for small businesses',
    features: ['1k leads/month', '5k emails', 'AI Insights', 'Chat & Email support', '+ Many more features'],
    badge: null,
    cardClass: 'bg-white border border-gray-200',
    textColor: 'text-blue-900',
    priceColor: 'text-blue-700',
    buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    selectedClass: 'ring-4 ring-blue-400 ring-offset-2'
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 384,
    description: 'Most popular for growing teams',
    features: ['5k leads/month', '25k emails', 'AI Insights', 'Priority Chat & Email', '5 custom integrations', '+ Many more features'],
    badge: 'Most Popular',
    badgeGradient: 'from-blue-500 to-purple-600',
    cardClass: 'bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-500',
    textColor: 'text-blue-600',
    priceColor: 'text-blue-600',
    buttonClass: 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white',
    selectedClass: 'ring-4 ring-blue-500 ring-offset-2'
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 520,
    description: 'Enterprise-grade solution',
    features: ['15k leads/month', '60k emails', 'AI Insights', 'Priority Support & Phone', '15 custom integrations', '+ Many more features'],
    badge: 'Advanced',
    badgeGradient: 'from-purple-500 to-pink-600',
    cardClass: 'bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-400',
    textColor: 'text-purple-600',
    priceColor: 'text-purple-600',
    buttonClass: 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white',
    selectedClass: 'ring-4 ring-purple-400 ring-offset-2'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 649,
    description: 'For large organizations',
    features: ['25k leads/month', '100k emails', 'AI Insights', 'Dedicated Support Manager', 'Unlimited integrations', '99.9% SLA', '+ Many more features'],
    badge: 'Enterprise',
    badgeGradient: 'from-gray-700 to-gray-900',
    cardClass: 'bg-white border border-gray-200',
    textColor: 'text-gray-900',
    priceColor: 'text-gray-800',
    buttonClass: 'bg-gray-900 hover:bg-gray-800 text-white',
    selectedClass: 'ring-4 ring-gray-400 ring-offset-2'
  }
];

const SubscriptionTiers = ({ selectedTier, onTierSelect }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {TIERS?.map((tier) => (
          <div
            key={tier?.id}
            onClick={() => onTierSelect?.(tier?.id)}
            className={`relative p-5 rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-105 ${
              selectedTier === tier?.id ? tier?.selectedClass : ''
            } ${tier?.cardClass}`}
          >
            {tier?.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-lg border-2 border-white bg-gradient-to-r ${tier?.badgeGradient} text-white whitespace-nowrap`}>
                  {tier?.badge}
                </div>
              </div>
            )}

            {selectedTier === tier?.id && (
              <div className="absolute top-3 right-3">
                <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              </div>
            )}

            <div className="text-center mb-4 pt-3">
              <h3 className={`text-lg font-bold mb-1 ${tier?.textColor}`}>{tier?.name}</h3>
              <p className="text-gray-500 text-xs mb-2">{tier?.description}</p>
              <div className="flex items-center justify-center space-x-1">
                {tier?.price === 0 ? (
                  <span className={`text-2xl font-bold ${tier?.priceColor}`}>Free</span>
                ) : (
                  <>
                    <span className={`text-2xl font-bold ${tier?.priceColor}`}>${tier?.price}</span>
                    <span className="text-gray-500 text-xs">/mo</span>
                  </>
                )}
              </div>
            </div>

            <ul className="space-y-1.5 mb-4">
              {tier?.features?.map((feature, fi) => (
                <li key={fi} className="flex items-start space-x-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 text-xs">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => onTierSelect?.(tier?.id)}
              className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all duration-300 shadow-md ${
                selectedTier === tier?.id
                  ? 'ring-2 ring-offset-1 ring-blue-400' :''
              } ${tier?.buttonClass}`}
            >
              {selectedTier === tier?.id ? '✓ Selected' : tier?.price === 0 ? 'Start Free' : 'Select Plan'}
            </button>
          </div>
        ))}
      </div>

      {/* Trust indicators */}
      <div className="flex flex-wrap items-center justify-center gap-6 pt-4 text-sm text-muted-foreground">
        <div className="flex items-center space-x-2">
          <Shield className="w-4 h-4 text-green-500" />
          <span>Secure payment</span>
        </div>
        <div className="flex items-center space-x-2">
          <Star className="w-4 h-4 text-yellow-500" />
          <span>Cancel anytime</span>
        </div>
        <div className="flex items-center space-x-2">
          <Headphones className="w-4 h-4 text-blue-500" />
          <span>24/7 support</span>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTiers;